import { IActionFolder, IProject, IScreenFolder } from "@kuika/kuika-cl-model";
import { ITableFolder } from "@kuika/kuika-cl-model/src/models/table-folder";
import { PartialContextProvider } from "../../../shared/patial/partial-context-provider";
import { FolderHelper } from "../../../shared/utility/folder-helper";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { IScreenDesignContextState } from "../screen-design-context";
import { IScreenDesignContextProviderProps, ScreenDesignContextProvider } from "../screen-design-context-provider";

export interface FolderProviderState {
  addScreenFolder: (folderName: string) => void;
  editScreenFolder: (folderId: string, folderName: string) => void;
  deleteScreenFolder: (folderId: string) => void;
  addScreenToFolder: (folderId: string, screenId: string) => void;
  removeScreenFromFolder: (folderId: string, screenId: string) => void;
  addScreenFolderInFolder: (folderId: string, folderName: string) => void;
  editScreenFolderInFolder: (folderId: string, folderInFolderId: string, newFolderName: string) => void;
  addScreenToFolderInFolder: (folderId: string, folderInFolderId: string, screenId: string) => void;
  removeScreenFromFolderInFolder: (folderId: string, folderInFolderId: string, screenId: string) => void;
  deleteScreenFolderInFolder: (folderId: string, folderInFolderId: string) => void;
  getFolderById: (folderId: string, folders: IScreenFolder[]) => IScreenFolder | undefined;
  findFolderByScreenId: (screenId: string, folders: IScreenFolder[]) => IScreenFolder | undefined;
  findFolderByActionId: (actionId: string, folders: IActionFolder[]) => IActionFolder | undefined;

  addRoleFolder?: (folderName: string) => void;
  editRoleFolder?: (folderId: string, folderName: string) => void;
  deleteRoleFolder?: (folderId: string) => void;
  addRoleToFolder?: (folderId: string, roleId: string) => void;
  removeRoleFromFolder?: (folderId: string, roleId: string) => void;
  addRoleFolderInFolder?: (folderId: string, folderName: string) => void;
  editRoleFolderInFolder?: (folderId: string, folderInFolderId: string, newFolderName: string) => void;
  deleteRoleFolderInFolder?: (folderId: string, folderInFolderId: string) => void;
  addRoleToFolderInFolder?: (folderId: string, folderInFolderId: string, roleId: string) => void;
  removeRoleFromFolderInFolder?: (folderId: string, folderInFolderId: string, roleId: string) => void;
  getRoleFolderById?: (folderId: string, folders: any[]) => any | undefined;
  getRoleFolderWithParent?: (folderId: string, folders: any[]) => { folder: any; parent: any | null } | undefined;
  findRoleFolderByRoleId?: (roleId: string, folders: any[]) => any | undefined;

  addActionFolder: (folderName: string, dataSourceType: string) => void;
  deleteActionFolder: (folderId: string) => void;
  editActionFolder: (folderId: string, folderName: string) => void;
  addActionToFolder: (folderId: string, actionId: string) => void;
  removeActionFromFolder: (folderId: string, actionId: string) => void;
  addActionFolderInFolder: (folderId: string, folderName: string) => void;
  editActionFolderInFolder: (folderId: string, folderInFolderId: string, newFolderName: string) => void;
  addActionToFolderInFolder: (folderId: string, folderInFolderId: string, screenId: string) => void;
  removeActionFromFolderInFolder: (folderId: string, folderInFolderId: string, screenId: string) => void;
  deleteActionFolderInFolder: (folderId: string, folderInFolderId: string) => void;

  addWorkFlowFolder: (folderName: string) => void;
  editWorkFlowFolder: (folderId: string, folderName: string) => void;
  deleteWorkFlowFolder: (folderId: string) => void;
  addWorkFlowToFolder: (folderId: string, wfId: string) => void;
  removeWorkFlowFromFolder: (folderId: string, wfId: string) => void;

  addDatabaseTableFolder: (folderName: string, dataSourceType: string) => void;
  deleteDatabaseTableFolder: (folderId: string) => void;
  editDatabaseTableFolder: (folderId: string, folderName: string) => void;
  addDatabaseTableToFolder: (folderId: string, tableId: string) => void;
  removeDatabaseTableFromFolder: (folderId: string, tableId: string) => void;
}

export class FolderProvider extends PartialContextProvider<
  ScreenDesignContextProvider,
  IScreenDesignContextProviderProps,
  IScreenDesignContextState,
  FolderProviderState
> {
  static defaultState: FolderProviderState = {
    addScreenFolder: () => {},
    editScreenFolder: () => {},
    deleteScreenFolder: () => {},
    addScreenToFolder: () => {},
    removeScreenFromFolder: () => {},
    addScreenFolderInFolder: () => {},
    editScreenFolderInFolder: () => {},
    addScreenToFolderInFolder: () => {},
    removeScreenFromFolderInFolder: () => {},
    deleteScreenFolderInFolder: () => {},
    getFolderById: () => undefined,
    findFolderByScreenId: () => undefined,
    findFolderByActionId: () => undefined,
    addRoleFolder: () => {},
    editRoleFolder: () => {},
    deleteRoleFolder: () => {},
    addRoleToFolder: () => {},
    removeRoleFromFolder: () => {},
    addRoleFolderInFolder: () => {},
    editRoleFolderInFolder: () => {},
    deleteRoleFolderInFolder: () => {},
    addRoleToFolderInFolder: () => {},
    removeRoleFromFolderInFolder: () => {},
    getRoleFolderById: () => undefined,
    getRoleFolderWithParent: () => undefined,
    findRoleFolderByRoleId: () => undefined,
    addActionFolder: () => {},
    deleteActionFolder: () => {},
    editActionFolder: () => {},
    addActionToFolder: () => {},
    removeActionFromFolder: () => {},
    addActionFolderInFolder: () => {},
    editActionFolderInFolder: () => {},
    addActionToFolderInFolder: () => {},
    removeActionFromFolderInFolder: () => {},
    deleteActionFolderInFolder: () => {},
    addWorkFlowFolder: () => {},
    editWorkFlowFolder: () => {},
    deleteWorkFlowFolder: () => {},
    addWorkFlowToFolder: () => {},
    removeWorkFlowFromFolder: () => {},
    addDatabaseTableFolder: () => {},
    deleteDatabaseTableFolder: () => {},
    editDatabaseTableFolder: () => {},
    addDatabaseTableToFolder: () => {},
    removeDatabaseTableFromFolder: () => {}
  };

  getProviderValues = (): FolderProviderState => {
    return {
      addScreenFolder: this.addScreenFolder,
      editScreenFolder: this.editScreenFolder,
      deleteScreenFolder: this.deleteScreenFolder,
      addScreenToFolder: this.addScreenToFolder,
      removeScreenFromFolder: this.removeScreenFromFolder,
      addScreenFolderInFolder: this.addScreenFolderInFolder,
      editScreenFolderInFolder: this.editScreenFolderInFolder,
      addScreenToFolderInFolder: this.addScreenToFolderInFolder,
      removeScreenFromFolderInFolder: this.removeScreenFromFolderInFolder,
      deleteScreenFolderInFolder: this.deleteScreenFolderInFolder,
      getFolderById: this.getFolderById,
      findFolderByScreenId: this.findFolderByScreenId,
      findFolderByActionId: this.findFolderByActionId,

      addRoleFolder: this.addRoleFolder,
      editRoleFolder: this.editRoleFolder,
      deleteRoleFolder: this.deleteRoleFolder,
      addRoleToFolder: this.addRoleToFolder,
      removeRoleFromFolder: this.removeRoleFromFolder,
      addRoleFolderInFolder: this.addRoleFolderInFolder,
      editRoleFolderInFolder: this.editRoleFolderInFolder,
      deleteRoleFolderInFolder: this.deleteRoleFolderInFolder,
      addRoleToFolderInFolder: this.addRoleToFolderInFolder,
      removeRoleFromFolderInFolder: this.removeRoleFromFolderInFolder,
      getRoleFolderById: this.getRoleFolderById,
      getRoleFolderWithParent: this.getRoleFolderWithParent,
      findRoleFolderByRoleId: this.findRoleFolderByRoleId,

      addActionFolder: this.addActionFolder,
      deleteActionFolder: this.deleteActionFolder,
      editActionFolder: this.editActionFolder,
      addActionToFolder: this.addActionToFolder,
      removeActionFromFolder: this.removeActionFromFolder,
      addActionFolderInFolder: this.addActionFolderInFolder,
      editActionFolderInFolder: this.editActionFolderInFolder,
      addActionToFolderInFolder: this.addActionToFolderInFolder,
      removeActionFromFolderInFolder: this.removeActionFromFolderInFolder,
      deleteActionFolderInFolder: this.deleteActionFolderInFolder,

      addWorkFlowFolder: this.addWorkFlowFolder,
      editWorkFlowFolder: this.editWorkFlowFolder,
      deleteWorkFlowFolder: this.deleteWorkFlowFolder,
      addWorkFlowToFolder: this.addWorkFlowToFolder,
      removeWorkFlowFromFolder: this.removeWorkFlowFromFolder,

      addDatabaseTableFolder: this.addDatabaseTableFolder,
      deleteDatabaseTableFolder: this.deleteDatabaseTableFolder,
      editDatabaseTableFolder: this.editDatabaseTableFolder,
      addDatabaseTableToFolder: this.addDatabaseTableToFolder,
      removeDatabaseTableFromFolder: this.removeDatabaseTableFromFolder
    };
  };

  private getProject = (): IProject => {
    return this.state.project;
  };

  private updateProject = async (project: IProject) => {
    await this.partial.projectProvider.updateProject(project);
  };

  addScreenFolder = (folderName: string) => {
    const project = this.getProject();
    if (!project.screenFolders) {
      project.screenFolders = [];
    }

    const result = FolderHelper.addFolder(project.screenFolders, folderName);
    if (result.success) {
      project.screenFolders = result.folders;
      this.updateProject(project);
    }
  };

  deleteScreenFolder = (folderId: string) => {
    const project = this.getProject();
    if (project.screenFolders) {
      project.screenFolders = FolderHelper.deleteFolder(project.screenFolders, folderId);
    }
    this.updateProject(project);
  };

  editScreenFolder = (folderId: string, folderName: string) => {
    const project = this.getProject();
    if (project.screenFolders) {
      project.screenFolders = FolderHelper.editFolder(project.screenFolders, folderId, folderName);
    }
    this.updateProject(project);
  };

  addScreenToFolder = (folderId: string, screenId: string) => {
    const project = this.getProject();

    if (!project.screenFolders) {
      project.screenFolders = [];
    }

    // Enforce single-folder membership: a screen lives in exactly one folder. Strip it from
    // every folder/sub-folder first (mirrors addRoleToFolder + addScreenToFolderInFolder), then
    // add to the target. Without the recursive strip a screen moved out of a sub-folder into a
    // top-level folder would remain in both → duplicate. (addItemToFolder only dedups top-level
    // siblings, not nested children.)
    project.screenFolders = FolderHelper.removeItemFromAllFolders(project.screenFolders, screenId, "screenIds");
    project.screenFolders = FolderHelper.addItemToFolder(
      project.screenFolders,
      folderId,
      screenId,
      "screenIds" as keyof IScreenFolder
    );

    this.updateProject(project);
  };

  removeScreenFromFolder = (folderId: string, screenId: string) => {
    const project = this.getProject();
    if (project.screenFolders) {
      project.screenFolders = FolderHelper.removeItemFromFolder(
        project.screenFolders,
        folderId,
        screenId,
        "screenIds" as keyof IScreenFolder
      );
    }
    this.updateProject(project);
  };

  addScreenFolderInFolder = (folderId: string, folderName: string) => {
    const project = this.getProject();
    if (!project.screenFolders) {
      return;
    }

    const result = FolderHelper.addChildFolder(project.screenFolders, folderId, folderName);
    if (result.success) {
      this.updateProject(project);
    }
  };

  deleteScreenFolderInFolder = (folderId: string, folderInFolderId: string) => {
    const project = this.getProject();
    if (project.screenFolders) {
      project.screenFolders = FolderHelper.deleteChildFolder(project.screenFolders, folderId, folderInFolderId);
      this.updateProject(project);
    }
  };

  editScreenFolderInFolder = (folderId: string, folderInFolderId: string, newFolderName: string) => {
    const project = this.getProject();
    if (project.screenFolders) {
      project.screenFolders = FolderHelper.editChildFolder(
        project.screenFolders,
        folderId,
        folderInFolderId,
        newFolderName
      );
      this.updateProject(project);
    }
  };

  addScreenToFolderInFolder = (folderId: string, folderInFolderId: string, screenId: string) => {
    const project = this.getProject();
    if (!project.screenFolders) {
      return;
    }

    project.screenFolders = FolderHelper.addItemToChildFolder(
      project.screenFolders,
      folderId,
      folderInFolderId,
      screenId,
      "screenIds"
    );
    this.updateProject(project);
  };

  removeScreenFromFolderInFolder = (folderId: string, folderInFolderId: string, screenId: string) => {
    const project = this.getProject();
    if (!project.screenFolders) {
      return;
    }

    project.screenFolders = FolderHelper.removeItemFromChildFolder(
      project.screenFolders,
      folderId,
      folderInFolderId,
      screenId,
      "screenIds"
    );
    this.updateProject(project);
  };

  getFolderById = (id: string, folders: any) => {
    return FolderHelper.getFolderById(id, folders);
  };

  findFolderByScreenId = (screenId: string, folders: IScreenFolder[]): IScreenFolder | undefined => {
    return FolderHelper.findFolderByItemId(screenId, folders, "screenIds");
  };

  findFolderByActionId = (id: string, folders: IActionFolder[]): IActionFolder | undefined => {
    return FolderHelper.findFolderByItemId(id, folders, "actionIds");
  };

  addRoleFolder = (folderName: string) => {
    const project = this.getProject();
    if (!project.roleFolders) {
      project.roleFolders = [] as any;
    }

    const result = FolderHelper.addFolder(project.roleFolders as any, folderName, {
      roleIds: [],
      children: []
    } as any);
    if (result.success) {
      project.roleFolders = result.folders;
      this.updateProject(project);
    }
  };

  editRoleFolder = (folderId: string, folderName: string) => {
    const project = this.getProject();
    if (!project.roleFolders) return;
    project.roleFolders = FolderHelper.editFolder(project.roleFolders as any, folderId, folderName);
    this.updateProject(project);
  };

  deleteRoleFolder = (folderId: string) => {
    const project = this.getProject();
    if (!project.roleFolders) return;
    project.roleFolders = FolderHelper.deleteFolder(project.roleFolders as any, folderId);
    this.updateProject(project);
  };

  addRoleToFolder = (folderId: string, roleId: string) => {
    const project = this.getProject();
    if (!project.roleFolders) project.roleFolders = [] as any;

    project.roleFolders = FolderHelper.removeItemFromAllFolders(project.roleFolders as any, roleId, "roleIds");
    project.roleFolders = FolderHelper.addItemToFolder(project.roleFolders as any, folderId, roleId, "roleIds" as any);

    this.updateProject(project);
  };

  removeRoleFromFolder = (folderId: string, roleId: string) => {
    const project = this.getProject();
    if (!project.roleFolders) return;
    project.roleFolders = FolderHelper.removeItemFromFolder(
      project.roleFolders as any,
      folderId,
      roleId,
      "roleIds" as any
    );
    this.updateProject(project);
  };

  addRoleFolderInFolder = (folderId: string, folderName: string) => {
    const project = this.getProject();
    if (!project.roleFolders) return;

    const result = FolderHelper.addChildFolder(project.roleFolders as any, folderId, folderName, {
      roleIds: [],
      children: []
    } as any);
    if (result.success) {
      this.updateProject(project);
    }
  };

  editRoleFolderInFolder = (folderId: string, folderInFolderId: string, newFolderName: string) => {
    const project = this.getProject();
    if (!project.roleFolders) return;
    project.roleFolders = FolderHelper.editChildFolder(
      project.roleFolders as any,
      folderId,
      folderInFolderId,
      newFolderName
    );
    this.updateProject(project);
  };

  deleteRoleFolderInFolder = (folderId: string, folderInFolderId: string) => {
    const project = this.getProject();
    if (!project.roleFolders) return;
    project.roleFolders = FolderHelper.deleteChildFolder(project.roleFolders as any, folderId, folderInFolderId);
    this.updateProject(project);
  };

  addRoleToFolderInFolder = (folderId: string, folderInFolderId: string, roleId: string) => {
    const project = this.getProject();
    if (!project.roleFolders) return;

    project.roleFolders = FolderHelper.removeItemFromAllFolders(project.roleFolders as any, roleId, "roleIds");
    project.roleFolders = FolderHelper.addItemToChildFolder(
      project.roleFolders as any,
      folderId,
      folderInFolderId,
      roleId,
      "roleIds"
    );

    this.updateProject(project);
  };

  removeRoleFromFolderInFolder = (folderId: string, folderInFolderId: string, roleId: string) => {
    const project = this.getProject();
    if (!project.roleFolders) return;
    project.roleFolders = FolderHelper.removeItemFromChildFolder(
      project.roleFolders as any,
      folderId,
      folderInFolderId,
      roleId,
      "roleIds"
    );
    this.updateProject(project);
  };

  getRoleFolderById = (folderId: string, folders: any[]): any | undefined => {
    return this.getFolderById(folderId, folders);
  };

  getRoleFolderWithParent = (folderId: string, folders: any[]): { folder: any; parent: any | null } | undefined => {
    return FolderHelper.getFolderWithParent(folderId, folders);
  };

  findRoleFolderByRoleId = (roleId: string, folders: any[]): any | undefined => {
    return FolderHelper.findFolderByItemId(roleId, folders, "roleIds");
  };

  addActionFolder = (folderName: string, dataSourceType: string) => {
    const project = this.getProject();
    if (!project.actionFolders) {
      project.actionFolders = [];
    }

    if (project.actionFolders?.some((x) => x.name === folderName && x.dataSourceType === dataSourceType)) {
      KMainFunctions.displayWarningNotification("This folder name already exists.");
      return;
    }

    const result = FolderHelper.addFolder(project.actionFolders, folderName, { dataSourceType });
    if (result.success) {
      project.actionFolders = result.folders;
      this.updateProject(project);
    }
  };

  deleteActionFolder = (folderId: string) => {
    const project = this.getProject();
    if (project.actionFolders) {
      project.actionFolders = FolderHelper.deleteFolder(project.actionFolders, folderId);
    }
    this.updateProject(project);
  };

  editActionFolder = (folderId: string, folderName: string) => {
    const project = this.getProject();
    if (project.actionFolders) {
      project.actionFolders = FolderHelper.editFolder(project.actionFolders, folderId, folderName);
    }
    this.updateProject(project);
  };

  addActionToFolder = (folderId: string, actionId: string) => {
    const project = this.getProject();

    if (!project.actionFolders) {
      project.actionFolders = [];
    }

    // Single-folder membership (same fix as addScreenToFolder/addRoleToFolder): action folders
    // support sub-folders, so an action moved out of a sub-folder into a top-level folder would
    // otherwise stay in both — addItemToFolder only dedups top-level siblings, not children.
    project.actionFolders = FolderHelper.removeItemFromAllFolders(project.actionFolders, actionId, "actionIds");
    project.actionFolders = FolderHelper.addItemToFolder(
      project.actionFolders,
      folderId,
      actionId,
      "actionIds" as keyof IActionFolder
    );

    this.updateProject(project);
  };

  removeActionFromFolder = (folderId: string, actionId: string) => {
    const project = this.getProject();
    if (project.actionFolders) {
      project.actionFolders = FolderHelper.removeItemFromFolder(
        project.actionFolders,
        folderId,
        actionId,
        "actionIds" as keyof IActionFolder
      );
    }
    this.updateProject(project);
  };

  addActionFolderInFolder = (folderId: string, folderName: string) => {
    const project = this.getProject();
    if (!project.actionFolders) {
      return;
    }

    const folder = project.actionFolders.find((x) => x.id === folderId);
    const result = FolderHelper.addChildFolder(project.actionFolders, folderId, folderName, {
      dataSourceType: folder?.dataSourceType
    });
    if (result.success) {
      this.updateProject(project);
    }
  };

  deleteActionFolderInFolder = (folderId: string, folderInFolderId: string) => {
    const project = this.getProject();
    if (project.actionFolders) {
      project.actionFolders = FolderHelper.deleteChildFolder(project.actionFolders, folderId, folderInFolderId);
      this.updateProject(project);
    }
  };

  editActionFolderInFolder = (folderId: string, folderInFolderId: string, newFolderName: string) => {
    const project = this.getProject();
    if (project.actionFolders) {
      project.actionFolders = FolderHelper.editChildFolder(
        project.actionFolders,
        folderId,
        folderInFolderId,
        newFolderName
      );
      this.updateProject(project);
    }
  };

  addActionToFolderInFolder = (folderId: string, folderInFolderId: string, actionId: string) => {
    const project = this.getProject();
    if (!project.actionFolders) {
      return;
    }

    project.actionFolders = FolderHelper.addItemToChildFolder(
      project.actionFolders,
      folderId,
      folderInFolderId,
      actionId,
      "actionIds"
    );
    this.updateProject(project);
  };

  removeActionFromFolderInFolder = (folderId: string, folderInFolderId: string, actId: string) => {
    const project = this.getProject();
    if (!project.actionFolders) {
      return;
    }

    project.actionFolders = FolderHelper.removeItemFromChildFolder(
      project.actionFolders,
      folderId,
      folderInFolderId,
      actId,
      "actionIds"
    );
    this.updateProject(project);
  };

  addWorkFlowFolder = (folderName: string) => {
    const project = this.getProject();
    if (!project.workFlowFolders) {
      project.workFlowFolders = [];
    }

    const result = FolderHelper.addFolder(project.workFlowFolders, folderName);
    if (result.success) {
      project.workFlowFolders = result.folders;
      this.updateProject(project);
    }
  };

  deleteWorkFlowFolder = (folderId: string) => {
    const project = this.getProject();
    if (project.workFlowFolders) {
      project.workFlowFolders = FolderHelper.deleteFolder(project.workFlowFolders, folderId);
    }
    this.updateProject(project);
  };

  editWorkFlowFolder = (folderId: string, folderName: string) => {
    const project = this.getProject();
    if (project.workFlowFolders) {
      project.workFlowFolders = FolderHelper.editFolder(project.workFlowFolders, folderId, folderName);
    }
    this.updateProject(project);
  };

  addWorkFlowToFolder = (folderId: string, wfId: string) => {
    const project = this.getProject();
    if (!project.workFlowFolders) {
      project.workFlowFolders = [];
    }

    // Single-folder membership (consistency with addScreenToFolder/addRoleToFolder). Workflow
    // folders have no sub-folders today, so addItemToFolder's sibling dedup already suffices —
    // the recursive strip is defensive and keeps every "add to folder" path uniform.
    project.workFlowFolders = FolderHelper.removeItemFromAllFolders(project.workFlowFolders, wfId, "workFlowIds");
    project.workFlowFolders = FolderHelper.addItemToFolder(
      project.workFlowFolders,
      folderId,
      wfId,
      "workFlowIds" as any
    );

    this.updateProject(project);
  };

  removeWorkFlowFromFolder = (folderId: string, wfId: string) => {
    const project = this.getProject();
    if (project.workFlowFolders) {
      project.workFlowFolders = FolderHelper.removeItemFromFolder(
        project.workFlowFolders,
        folderId,
        wfId,
        "workFlowIds" as any
      );
    }
    this.updateProject(project);
  };

  addDatabaseTableFolder = (folderName: string, dataSourceType: string) => {
    const project = this.getProject();
    if (!project.databaseTablesFolders) {
      project.databaseTablesFolders = [];
    }

    const result = FolderHelper.addFolder(project.databaseTablesFolders, folderName, { dataSourceType });
    if (result.success) {
      project.databaseTablesFolders = result.folders;
      this.updateProject(project);
    }
  };

  deleteDatabaseTableFolder = (folderId: string) => {
    const project = this.getProject();
    if (project.databaseTablesFolders) {
      project.databaseTablesFolders = FolderHelper.deleteFolder(project.databaseTablesFolders, folderId);
    }
    this.updateProject(project);
  };

  editDatabaseTableFolder = (folderId: string, folderName: string) => {
    const project = this.getProject();
    if (project.databaseTablesFolders) {
      project.databaseTablesFolders = FolderHelper.editFolder(project.databaseTablesFolders, folderId, folderName);
    }
    this.updateProject(project);
  };

  addDatabaseTableToFolder = (folderId: string, tableId: string): void => {
    const project = this.getProject();

    if (!project.databaseTablesFolders) {
      project.databaseTablesFolders = [];
    }

    // Single-folder membership (consistency with addScreenToFolder/addRoleToFolder), within the
    // target folder's datasource only. A table's member id (`getTableFolderMemberId`) is unique per
    // datasource, not per project: a connected server's id-less table is filed as `schema.name`,
    // which a second connection to a copy of the same database shares. The generic helpers strip
    // the id from every folder in the list, so this one scopes the strip itself. Table folders do
    // not nest, so there are no sub-folders to walk.
    const target: ITableFolder | undefined = project.databaseTablesFolders.find(
      (folder: ITableFolder): boolean => folder.id === folderId
    );
    if (!target) return;
    project.databaseTablesFolders = project.databaseTablesFolders.map((folder: ITableFolder): ITableFolder => {
      if (folder.dataSourceType !== target.dataSourceType) return folder;
      const tableIds: string[] = folder.tableIds ?? [];
      if (folder.id === folderId) {
        return tableIds.includes(tableId) ? folder : { ...folder, tableIds: [...tableIds, tableId] };
      }
      return { ...folder, tableIds: tableIds.filter((id: string): boolean => id !== tableId) };
    });

    this.updateProject(project);
  };

  removeDatabaseTableFromFolder = (folderId: string, tableId: string) => {
    const project = this.getProject();
    if (project.databaseTablesFolders) {
      project.databaseTablesFolders = FolderHelper.removeItemFromFolder(
        project.databaseTablesFolders,
        folderId,
        tableId,
        "tableIds" as any
      );
    }
    this.updateProject(project);
  };
}
